import { useState } from "react";
import { ChevronRight, RotateCcw, CheckCircle, XCircle } from "lucide-react";

interface Answer {
  question: number;
  selected: number;
  correct: boolean;
}

const ConditionalProbabilityQuiz = () => {
  const questions = [
    {
      id: 1,
      question: "Card Deck Classic",
      description:
        "A card is drawn from a standard deck. What's the probability it's a spade given it's a face card (Jack, Queen, King)?",
      options: ["1/4", "1/3", "3/13", "1/2"],
      correct: 0,
      explanation:
        "There are 12 face cards total, and 3 of them are spades. So P(Spade|Face) = 3/12 = 1/4",
    },
    {
      id: 2,
      question: "Rainy Day",
      description:
        "In your city, it rains 10% of the days. You hear the weather app beep, which correctly predicts rain 80% of the time when it actually rains, but also beeps (false alarm) 10% of the time when it's not raining. If you hear the beep, what's the probability it's actually raining?",
      options: ["≈ 0.47", "≈ 0.80", "≈ 0.10", "≈ 0.25"],
      correct: 0,
      explanation:
        "Using Bayes' theorem: P(Rain|Beep) = (0.8 × 0.1) / (0.8 × 0.1 + 0.1 × 0.9) ≈ 0.47",
    },
    {
      id: 3,
      question: "Two Dice",
      description:
        "You roll two dice. What is the probability that both show a 6, given that at least one shows a 6?",
      options: ["1/11", "1/6", "1/36", "2/11"],
      correct: 0,
      explanation:
        "P(Both 6|At least one 6) = P(Both 6) / P(At least one 6) = (1/36) / (11/36) = 1/11",
    },
    {
      id: 4,
      question: "Blue-Eyed Family",
      description:
        "A family has two children. What's the probability that both children have blue eyes given that at least one has blue eyes? (Assume each child has a 25% chance of blue eyes, independent of the other.)",
      options: ["1/7", "1/4", "1/3", "1/2"],
      correct: 0,
      explanation:
        "P(Both blue|At least one blue) = 0.25² / (1 - 0.75²) = 0.0625 / 0.4375 = 1/7",
    },
    {
      id: 5,
      question: "Classroom Confusion",
      description:
        "In a class, 70% of students are right-handed, 30% are left-handed. On a test, 60% of right-handed and 90% of left-handed students pass. A student is picked at random from those who passed. What's the probability they are left-handed?",
      options: ["≈ 0.39", "≈ 0.30", "≈ 0.90", "≈ 0.50"],
      correct: 0,
      explanation:
        "P(Left|Pass) = (0.9 × 0.3) / (0.6 × 0.7 + 0.9 × 0.3) = 0.27 / 0.69 ≈ 0.39",
    },
    {
      id: 6,
      question: "Medical Test",
      description:
        "A disease affects 1 in 1000 people. The test for the disease is 99% accurate (both for positives and negatives). If a randomly chosen person tests positive, what's the probability they actually have the disease?",
      options: ["≈ 0.09", "≈ 0.99", "≈ 0.50", "≈ 0.01"],
      correct: 0,
      explanation:
        "P(Disease|Positive) = (0.99 × 0.001) / (0.99 × 0.001 + 0.01 × 0.999) ≈ 0.09 or 9%",
    },
    {
      id: 7,
      question: "Marble Mystery",
      description:
        "A bag contains 3 red marbles and 7 green marbles. You pick one at random, don't look, and then pick a second at random. What is the probability the second is red given the first was red?",
      options: ["2/9", "3/10", "1/3", "3/9"],
      correct: 0,
      explanation:
        "After removing one red marble, there are 2 red and 7 green left. So P(2nd red|1st red) = 2/9",
    },
    {
      id: 8,
      question: "Prisoner's Dilemma",
      description:
        "Three prisoners, A, B, and C, are told that one of them will be freed at random. A asks the guard privately who, other than A, will be executed, and the guard truthfully says 'B.' What is the probability that A will be freed given this answer?",
      options: ["1/2", "1/3", "2/3", "1/4"],
      correct: 0,
      explanation:
        "This is a classic problem. The guard's answer doesn't change A's probability, which remains 1/2 (between A and C)",
    },
    {
      id: 9,
      question: "Monty Hall Redux",
      description:
        "You're on a game show with 3 doors: behind one is a car, behind two are goats. You pick Door 1. The host, who knows what's behind the doors, opens Door 3 to reveal a goat. What's the probability the car is behind Door 2, given what you know?",
      options: ["2/3", "1/2", "1/3", "3/4"],
      correct: 0,
      explanation:
        "The famous Monty Hall problem! Switching gives you a 2/3 chance of winning the car.",
    },
    {
      id: 10,
      question: "Spy vs. Spy",
      description:
        "You're told a spy uses a secure phone 20% of the time. When using a secure phone, messages are encrypted 90% of the time; otherwise, only 30% of the time. If you intercept an encrypted message, what's the probability the spy was using a secure phone?",
      options: ["≈ 0.43", "≈ 0.20", "≈ 0.90", "≈ 0.60"],
      correct: 0,
      explanation:
        "P(Secure|Encrypted) = (0.9 × 0.2) / (0.9 × 0.2 + 0.3 × 0.8) = 0.18 / 0.42 ≈ 0.43",
    },
  ];

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const handleAnswerSelect = (index: number) => {
    if (!showResult) {
      setSelectedAnswer(index);
    }
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === questions[currentQuestion].correct;
    if (isCorrect) {
      setScore(score + 1);
    }

    setAnswers([
      ...answers,
      {
        question: currentQuestion,
        selected: selectedAnswer,
        correct: isCorrect,
      },
    ]);
    setShowResult(true);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizComplete(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setQuizComplete(false);
    setAnswers([]);
  };

  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "Perfect! You're a probability master! 🎉";
    if (percentage >= 80)
      return "Excellent work! You really understand conditional probability! 🌟";
    if (percentage >= 60)
      return "Good job! You've got a solid grasp of the concepts. 👏";
    if (percentage >= 40)
      return "Not bad! Keep practicing to improve your skills. 💪";
    return "Keep studying! Conditional probability can be tricky. 📚";
  };

  if (quizComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
          <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Quiz Complete! 🎊
          </h1>

          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 mb-6 text-white">
            <p className="text-6xl font-bold text-center mb-2">
              {score}/{questions.length}
            </p>
            <p className="text-xl text-center">{getScoreMessage()}</p>
          </div>

          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {answers.map((answer, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  answer.correct ? "bg-green-50" : "bg-red-50"
                }`}
              >
                <span className="font-medium">
                  Question {idx + 1}: {questions[idx].question}
                </span>
                {answer.correct ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <XCircle className="text-red-600" size={24} />
                )}
              </div>
            ))}
          </div>

          <button
            onClick={resetQuiz}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <RotateCcw size={20} />
            Take Quiz Again
          </button>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium text-gray-500">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <span className="text-sm font-medium text-gray-500">
              Score: {score}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${((currentQuestion + 1) / questions.length) * 100}%`,
              }}
            />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-3">
          {question.question}
        </h2>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {question.description}
        </p>

        <div className="space-y-3 mb-6">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              disabled={showResult}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                showResult
                  ? index === question.correct
                    ? "border-green-500 bg-green-50"
                    : index === selectedAnswer
                    ? "border-red-500 bg-red-50"
                    : "border-gray-200 bg-gray-50"
                  : selectedAnswer === index
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{option}</span>
                {showResult && index === question.correct && (
                  <CheckCircle className="text-green-600" size={20} />
                )}
                {showResult &&
                  index === selectedAnswer &&
                  index !== question.correct && (
                    <XCircle className="text-red-600" size={20} />
                  )}
              </div>
            </button>
          ))}
        </div>

        {showResult && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Explanation:</span>{" "}
              {question.explanation}
            </p>
          </div>
        )}

        {!showResult ? (
          <button
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
            className={`w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 ${
              selectedAnswer === null
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
            }`}
          >
            Submit Answer
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2"
          >
            {currentQuestion < questions.length - 1
              ? "Next Question"
              : "See Results"}
            <ChevronRight size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ConditionalProbabilityQuiz;
